import React, { useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { columnDefs, defaultColDef } from '../_utils/definitions-config';
import { getPaymentFlowsList } from '../../../services/apiService';

// ─── Status pill cell renderer ────────────────────────────────────
function StatusPillRenderer(props) {
  const status = props?.data?.paymentLinkStatus;
  const map = {
    created:  { bg: '#EEF2FF', color: '#4F46E5', border: '#C7D2FE', label: 'Created' },
    paid:     { bg: '#ECFDF5', color: '#059669', border: '#A7F3D0', label: 'Paid' },
    expired:  { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA', label: 'Expired' },
    cancelled: { bg: '#F9FAFB', color: '#6B7280', border: '#E5E7EB', label: 'Cancelled' },
  };
  const style = map[status] || map.cancelled;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 10px',
        borderRadius: '9999px',
        fontSize: 11.5,
        fontWeight: 600,
        backgroundColor: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        lineHeight: 1.6,
      }}
    >
      {style.label}
    </span>
  );
}

// ─── Avatar + name cell renderer ─────────────────────────────────
function AvatarNameRenderer(props) {
  const data = props?.data;
  const firstName = data?.CreatedBy?.firstName || '';
  const lastName = data?.CreatedBy?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const initials = fullName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const colours = ['#EEF2FF', '#E0F2FE', '#D1FAE5', '#FEF3C7', '#FCE7F3'];
  const textColours = ['#4F46E5', '#0369A1', '#047857', '#B45309', '#BE185D'];
  const hash = fullName ? fullName.charCodeAt(0) % colours.length : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%' }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          backgroundColor: colours[hash],
          color: textColours[hash],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {initials || '—'}
      </div>
      <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
        {fullName || '—'}
      </span>
    </div>
  );
}

// ─── Reusable table card wrapper ──────────────────────────────────
function TableCard({ title, count, toolbar, children }) {
  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: 16,
        border: '1px solid rgba(0,0,0,0.07)',
        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}
    >
      {/* Card header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid #F3F4F6',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
            {title}
          </span>
          {count !== undefined && (
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                color: '#6B7280',
                backgroundColor: '#F3F4F6',
                border: '1px solid #E5E7EB',
                borderRadius: '9999px',
                padding: '2px 10px',
              }}
            >
              {count} records
            </span>
          )}
        </div>
        {toolbar}
      </div>

      {/* AG Grid */}
      <div
        className="ag-theme-alpine"
        style={{ height: 520, width: '100%' }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── Payment Link Table ───────────────────────────────────────────
function PaymentLinkTable({ refreshKey }) {
  const [gridApi, setGridApi] = React.useState(null);
  const [totalCount, setTotalCount] = React.useState(0);

  const pageSize = 10;

  const getServerSideDatasource = useCallback(() => {
    return {
      getRows: (params) => {
        try {
          getPaymentFlowsList(
            params.request.startRow / pageSize + 1,
            pageSize
          ).then((response) => {
            const rows = response?.data || [];
            const total = response?.totalCount || 0;
            setTotalCount(total);
            params.successCallback(rows, total);
          });
        } catch (error) {
          console.error(error);
          params.failCallback();
        }
      },
    };
  }, []);

  const onGridReady = useCallback(
    (params) => {
      setGridApi(params);
      const datasource = getServerSideDatasource();
      params.api.setServerSideDatasource(datasource);
    },
    [getServerSideDatasource]
  );

  // Refresh when refreshKey changes
  React.useEffect(() => {
    if (gridApi) {
      const datasource = getServerSideDatasource();
      gridApi.api.setServerSideDatasource(datasource);
    }
  }, [refreshKey]);

  // Column defs with custom renderers
  const allColumnDefs = useMemo(
    () => columnDefs(onGridReady, gridApi),
    [onGridReady, gridApi]
  );

  // Override specific columns to use new renderers
  const styledColumnDefs = useMemo(() => {
    return allColumnDefs.map((col) => {
      if (col.field === 'name') {
        return { ...col, cellRenderer: AvatarNameRenderer };
      }
      if (col.field === 'linkStatus') {
        return { ...col, cellRenderer: StatusPillRenderer };
      }
      return col;
    });
  }, [allColumnDefs]);

  return (
    <TableCard
      title="Payment Links"
      count={totalCount}
    >
      <AgGridReact
        columnDefs={styledColumnDefs}
        defaultColDef={defaultColDef}
        tooltipShowDelay={0}
        tooltipHideDelay={2000}
        onGridReady={onGridReady}
        domLayout="normal"
        rowModelType="serverSide"
        rowHeight={52}
        headerHeight={40}
        serverSideStoreType="partial"
        animateRows={true}
        serverSideInfiniteScroll={true}
        pagination={true}
        suppressPaginationPanel={false}
        paginationPageSize={pageSize}
        cacheBlockSize={pageSize}
        suppressCellFocus
      />
    </TableCard>
  );
}

export default PaymentLinkTable;
